// What the Readiness Rate chart plots, one row per calendar day of the period: the total and its
// status where there is a questionnaire, the day's normal as a band, and the overlay values (Session RPE, HRV, Resting HR) wherever they exist.

import { dayNumToUtc, utcToDayNum } from "./dates";

const OVERLAY_KEYS = [
  { key: "sessionRpe", field: "sessionRpe" },
  { key: "hrv", field: "hrv" },
  { key: "restingHr", field: "restingHr" },
];

const indexByUtc = (list) => new Map(list.map((item) => [item.utcDate, item]));

// Which overlays have anything to show in the period (the others are not offered)
export const availableOverlays = (report) =>
  OVERLAY_KEYS.filter(({ key }) => report.overlays[key].length > 0).map(
    ({ key }) => key
  );

/**
 * Builds the chart rows for the readiness report.
 * @param {*} report
 * @returns
 */
export const buildChartRows = (report) => {
  const fromDay = utcToDayNum(report.period.fromUtc);
  const toDay = utcToDayNum(report.period.toUtc);
  const daysByUtc = indexByUtc(report.days);
  const bandByUtc = indexByUtc(report.normalBand);
  const overlayByUtc = Object.fromEntries(
    OVERLAY_KEYS.map(({ key }) => [key, indexByUtc(report.overlays[key])])
  );

  const rows = [];
  for (let day = fromDay; day <= toDay; day++) {
    const utcDate = dayNumToUtc(day);
    const entry = daysByUtc.get(utcDate);
    const band = bandByUtc.get(utcDate);
    const isQuestionnaire = entry?.kind === "questionnaire";
    const hrvPoint = overlayByUtc.hrv.get(utcDate);
    rows.push({
      idx: day - fromDay,
      utcDate,
      total: isQuestionnaire ? entry.total : null,
      status: isQuestionnaire ? entry.status : null,
      answers: isQuestionnaire ? entry.answers : null,
      band: band ? [band.lo, band.hi] : null,
      sessionRpe: overlayByUtc.sessionRpe.get(utcDate)?.value ?? null,
      hrv: hrvPoint?.value ?? null,
      hrvSource: hrvPoint?.source ?? null,
      restingHr: overlayByUtc.restingHr.get(utcDate)?.value ?? null,
    });
  }
  return bridgeLines(rows);
};

// `<field>Line`: the values of a field, each joined in a straight line to the next one when they
// are, so a gap between two readings is still a line. The field itself
// stays as it is: it carries the dots and the tooltip. Used for the score and for every overlay.
const bridgeLine = (rows, field, lineField) => {
  for (const row of rows) row[lineField] = row[field];
  let previous = null;
  for (let i = 0; i < rows.length; i++) {
    if (rows[i][field] === null) continue;
    const gap = previous === null ? 0 : i - previous;
    if (gap > 1) {
      const from = rows[previous][field];
      const to = rows[i][field];
      for (let day = previous + 1; day < i; day++) {
        rows[day][lineField] = from + ((to - from) * (day - previous)) / gap;
      }
    }
    previous = i;
  }
  return rows;
};

const bridgeLines = (rows) => {
  bridgeLine(rows, "total", "scoreLine");
  for (const { key } of OVERLAY_KEYS) {
    bridgeLine(rows, key, `${key}Line`);
  }
  return rows;
};

// X-axis ticks: about 6 to 8 evenly spaced days, on a step that reads well in dates
const TICK_STEPS = [1, 2, 7, 14, 30, 60, 91, 182, 365];
const MAX_TICKS = 8;

export const buildXTicks = (rowCount) => {
  const last = Math.max(rowCount - 1, 0);
  const step =
    TICK_STEPS.find(
      (candidate) => Math.floor(last / candidate) + 1 <= MAX_TICKS
    ) ?? TICK_STEPS.at(-1);
  const ticks = [];
  for (let idx = 0; idx <= last; idx += step) ticks.push(idx);
  return ticks;
};
